import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Factory } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import type { Supplier } from '@/lib/types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState, Modal, Toggle } from '@/components/ui/primitives'
import { t } from '@/i18n'

interface Draft {
  name: string
  contactName: string
  phone: string
  email: string
  address: string
  paymentTermDays: string
  notes: string
  isActive: boolean
}

const empty: Draft = {
  name: '',
  contactName: '',
  phone: '',
  email: '',
  address: '',
  paymentTermDays: '0',
  notes: '',
  isActive: true,
}

export function SuppliersSection() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Supplier | null>(null)
  const [isOpen, setOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>(empty)
  const [error, setError] = useState<string | null>(null)

  const suppliers = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => apiClient.get<Supplier[]>('/api/suppliers'),
  })

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: draft.name.trim(),
        contactName: draft.contactName.trim() || null,
        phone: draft.phone.trim() || null,
        email: draft.email.trim() || null,
        address: draft.address.trim() || null,
        paymentTermDays: Number(draft.paymentTermDays || 0),
        notes: draft.notes.trim() || null,
        isActive: draft.isActive,
      }
      return editing
        ? apiClient.put(`/api/suppliers/${editing.id}`, body)
        : apiClient.post('/api/suppliers', body)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      setOpen(false)
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save.'),
  })

  function open(supplier: Supplier | null) {
    setEditing(supplier)
    setDraft(
      supplier
        ? {
            name: supplier.name,
            contactName: supplier.contactName ?? '',
            phone: supplier.phone ?? '',
            email: supplier.email ?? '',
            address: supplier.address ?? '',
            paymentTermDays: String(supplier.paymentTermDays),
            notes: supplier.notes ?? '',
            isActive: supplier.isActive,
          }
        : empty,
    )
    setError(null)
    setOpen(true)
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">
          {t('Who you buy from. Payment terms drive the payables ageing view.')}
        </p>
        <Button size="sm" onClick={() => open(null)}>
          <Plus className="h-4 w-4" />
          {t('New')}
        </Button>
      </div>

      {suppliers.data?.length === 0 ? (
        <EmptyState
          icon={Factory}
          title={t('No suppliers yet')}
          description={t('You need at least one before you can receive a delivery.')}
          action={
            <Button onClick={() => open(null)}>
              <Plus className="h-4 w-4" />
              {t('New supplier')}
            </Button>
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {suppliers.data?.map((supplier) => (
            <li key={supplier.id}>
              <button
                type="button"
                onClick={() => open(supplier)}
                className="surface-raised flex w-full items-center gap-3 rounded-[var(--radius-card)] border p-3 text-left hover:border-[var(--accent)]"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{supplier.name}</p>
                  <p className="truncate text-xs text-[var(--text-secondary)]">
                    {[supplier.contactName, supplier.phone].filter(Boolean).join(' · ') ||
                      'No contact details'}
                  </p>
                </div>
                {!supplier.isActive && <Badge>{t('Inactive')}</Badge>}
                {supplier.paymentTermDays > 0 && (
                  <Badge tone="neutral">{supplier.paymentTermDays}d terms</Badge>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={isOpen}
        onClose={() => setOpen(false)}
        title={editing ? `Edit ${editing.name}` : 'New supplier'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || !draft.name.trim()}>
              {t('Save')}
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('Name')}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder={t('Liban Lait')}
            required
          />
          <Input
            label={t('Contact')}
            value={draft.contactName}
            onChange={(e) => setDraft({ ...draft, contactName: e.target.value })}
          />
          <Input
            label={t('Phone')}
            type="tel"
            value={draft.phone}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
            className="tabular"
          />
          <Input
            label={t('Email')}
            type="email"
            value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          />
          <Input
            label={t('Payment terms (days)')}
            type="number"
            inputMode="numeric"
            min="0"
            value={draft.paymentTermDays}
            onChange={(e) => setDraft({ ...draft, paymentTermDays: e.target.value })}
            className="tabular"
          />
          <div className="flex items-end">
            <Toggle
              checked={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              label={t('Active')}
            />
          </div>
        </div>

        <Input
          label={t('Address')}
          value={draft.address}
          onChange={(e) => setDraft({ ...draft, address: e.target.value })}
          wrapperClassName="mt-4"
        />
        <Input
          label={t('Notes')}
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          wrapperClassName="mt-4"
        />

        {error && (
          <p role="alert" className="mt-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
            {error}
          </p>
        )}
      </Modal>
    </div>
  )
}
